// Data Health — the post-save reconciliation rule.
//
// After a fix lands, a Data Health list has to answer three questions:
//
//   1. Which rows leave the list?      → the ones the SERVER named resolved.
//   2. What is the new row total?      → minus the rows that actually left.
//   3. Does the list need re-reading?  → yes if a write left rows on screen
//      whose values it changed, or if the visible buffer emptied while the
//      server still holds rows.
//
// Question 3 is the subtle one. A successful write is NOT a resolution (see
// ./resolveIssue.js): for price-below-cost a price write can succeed and leave
// the row exactly where it was, still rendering the cost/price it had BEFORE
// the write. Miss that and the merchant's edit looks like it vanished.
//
// This module does not own the re-read: it says whether one is owed
// (`needsReread`), and the caller decides how to re-read its rows.

/**
 * Did this save leave rows on screen whose values it changed?
 *
 * True when a row's write landed but the server did not resolve it: the row
 * stays, still rendering what it held BEFORE the write, so the list has to
 * re-read it. Patching the posted values into the cached rows instead would
 * put a second source of truth for row data in the client — the exact guess
 * `resolvedIds` exists to remove.
 *
 * A property of the OUTCOME alone, so a caller can ask without holding the
 * rows (DataHealth.jsx's per-row action does). `reconcileRows` folds it into
 * `needsReread`.
 */
export function hasStaleValues(outcome) {
  for (const id of outcome.successIds) {
    if (!outcome.resolvedIds.has(id)) return true;
  }
  return false;
}

/**
 * Apply a save outcome to the rows on screen.
 *
 * @param {object[]} rows  The rows currently on screen, keyed by `row.id`.
 * @param {{successIds: Set<string>, resolvedIds: Set<string>}} outcome
 *   From `resolveIssue`. `successIds` = the write landed; `resolvedIds` = the
 *   server re-checked the issue's population and these rows are out of it.
 * @param {object} opts
 * @param {number} opts.totalItems  The list's current server-reported total.
 *
 * @returns {{
 *   remaining: object[], removed: number, totalItems: number,
 *   needsReread: boolean,
 * }}
 *   `needsReread` is true when a write left rows on screen whose values it
 *   changed, or when the buffer emptied while the server still holds rows.
 *   `totalItems` drops by the rows that ACTUALLY left this list, not by
 *   `resolvedIds.size` — a resolved id the list never held (it can't normally
 *   happen; a save only posts loaded rows) would otherwise shrink the total
 *   without a row leaving, and the list would claim fewer rows than exist.
 *   Under-counting is the safe direction: the next read corrects it.
 */
export function reconcileRows(rows, outcome, { totalItems }) {
  const remaining = rows.filter((row) => !outcome.resolvedIds.has(String(row.id)));
  const removed = rows.length - remaining.length;
  const newTotal = Math.max(0, totalItems - removed);
  const exhausted = remaining.length === 0 && newTotal > 0;

  return {
    remaining,
    removed,
    totalItems: newTotal,
    needsReread: hasStaleValues(outcome) || exhausted,
  };
}
